import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Download, Upload, AlertTriangle, CheckCircle2, Info, ShieldCheck, X,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { buildQRValue, makeQR, verifyQR, contrastRatio, scaledQuietZone } from "@/lib/qr";

const PREVIEW_SIZE = 300;

const TYPE_GROUPS = [
  {
    label: "Links & Research",
    items: [
      { value: "url", label: "Website Link" },
      { value: "doi", label: "DOI Link" },
      { value: "article", label: "Journal Article" },
      { value: "survey", label: "Online Survey" },
      { value: "pdf", label: "PDF / Document Link" },
      { value: "orcid", label: "ORCID Profile" },
    ],
  },
  {
    label: "Contact & Text",
    items: [
      { value: "email", label: "Email Address" },
      { value: "phone", label: "Phone Number" },
      { value: "text", label: "Plain Text" },
      { value: "wifi", label: "Wi-Fi Credentials" },
      { value: "vcard", label: "Digital Contact Card" },
    ],
  },
];

const ALL_TYPE_VALUES = TYPE_GROUPS.flatMap((g) => g.items.map((i) => i.value));

function typeFromQuery() {
  try {
    const t = new URLSearchParams(window.location.search).get("type");
    return t && ALL_TYPE_VALUES.includes(t) ? t : null;
  } catch { return null; }
}


const PLACEHOLDERS = {
  url: "https://example.edu/my-project",
  doi: "10.1000/xyz123 or https://doi.org/10.1000/xyz123",
  article: "https://journal.org/article/12345",
  survey: "https://survey.example.com/s/abc",
  pdf: "https://example.edu/poster.pdf",
  orcid: "0000-0002-1825-0097",
  email: "researcher@university.edu",
  phone: "+1 555 123 4567",
  text: "Any plain text to encode",
};

function LabeledInput({ id, label, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} {...props} />
    </div>
  );
}

export default function QRStudio({ fixedValue = null, fixedType = null, downloadName = "qr", compact = false, initialStyle = null, onStyleChange = null }) {
  const containerRef = useRef(null);
  const qrRef = useRef(null);

  const [type, setType] = useState(fixedType || typeFromQuery() || "url");
  const [fields, setFields] = useState({ value: "", encryption: "WPA" });
  const [fgColor, setFgColor] = useState(initialStyle?.fgColor || "#0A2540");
  const [bgColor, setBgColor] = useState(initialStyle?.bgColor || "#FFFFFF");
  const [dotType, setDotType] = useState(initialStyle?.dotType || "square");
  const [cornerType, setCornerType] = useState(initialStyle?.cornerType || "square");
  const [resolution, setResolution] = useState(512);
  const [margin, setMargin] = useState(initialStyle?.margin ?? 16);
  const [ecLevel, setEcLevel] = useState(initialStyle?.ecLevel || "M");
  const [logo, setLogo] = useState(initialStyle?.logo || null);
  const [verifying, setVerifying] = useState(false);

  // Report style changes upward (skip the initial mount) so the parent can persist them.
  const styleMounted = useRef(false);
  useEffect(() => {
    if (!onStyleChange) return;
    if (!styleMounted.current) { styleMounted.current = true; return; }
    onStyleChange({ fgColor, bgColor, dotType, cornerType, margin, ecLevel, logo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fgColor, bgColor, dotType, cornerType, margin, ecLevel, logo]);

  const qrValue = useMemo(() => {
    if (fixedValue) return fixedValue;
    return buildQRValue(type, fields);
  }, [fixedValue, type, fields]);

  const previewOptions = useMemo(
    () => ({
      data: qrValue || " ",
      size: 300,
      fgColor,
      bgColor,
      dotType,
      cornerType,
      margin,
      errorCorrectionLevel: ecLevel,
      logo,
    }),
    [qrValue, fgColor, bgColor, dotType, cornerType, margin, ecLevel, logo]
  );

  useEffect(() => {
    qrRef.current = makeQR(previewOptions);
    if (containerRef.current) {
      containerRef.current.innerHTML = "";
      qrRef.current.append(containerRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (qrRef.current) qrRef.current.update(previewOptions);
  }, [previewOptions]);

  const ratio = useMemo(() => contrastRatio(fgColor, bgColor), [fgColor, bgColor]);
  const lowContrast = ratio < 3;
  const logoNeedsEC = logo && (ecLevel === "L" || ecLevel === "M");

  const setField = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const onLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error("Logo image must be under 1 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setLogo(reader.result);
    reader.readAsDataURL(file);
    if (ecLevel === "L" || ecLevel === "M") setEcLevel("H");
  };

  const doDownload = async (kind) => {
    if (!qrValue || !qrValue.trim()) {
      toast.error("Add content before downloading.");
      return;
    }
    setVerifying(true);
    try {
      let ext = "png";
      let dlSize = resolution;
      let transparent = false;
      if (kind === "svg") ext = "svg";
      if (kind === "hires") dlSize = 2048;
      if (kind === "transparent") transparent = true;
      // Preserve the on-screen quiet-zone RATIO at the output size so the
      // downloaded code scans exactly like the preview (fixes 2048px failures).
      const dlMargin = scaledQuietZone(margin, PREVIEW_SIZE, dlSize);
      const dlOpts = { data: qrValue, fgColor, bgColor, dotType, cornerType, margin: dlMargin, errorCorrectionLevel: ecLevel, logo, size: dlSize, transparent };
      const res = await verifyQR(qrValue, dlOpts);
      if (!res.ok) {
        toast.error(res.reason + " Try increasing contrast or error correction.");
        return;
      }
      const inst = makeQR(dlOpts);
      await inst.download({ name: `${downloadName}-${type}`, extension: ext });
      toast.success("QR code verified and downloaded.");
    } catch (e) {
      toast.error("Download failed: " + (e?.message || "unknown error"));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className={compact ? "space-y-6" : "grid grid-cols-1 gap-6 lg:grid-cols-5"}>
      {/* Left: config */}
      <div className={compact ? "space-y-6" : "space-y-6 lg:col-span-3"}>
        {!fixedValue && (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="space-y-4 p-6">
              <div className="space-y-1.5">
                <Label htmlFor="qr-type">QR code type</Label>
                <Select value={type} onValueChange={(v) => { setType(v); setFields({ value: "", encryption: "WPA" }); }}>
                  <SelectTrigger id="qr-type" data-testid="qr-type-select">
                    <SelectValue placeholder="Select a type" />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_GROUPS.map((g) => (
                      <SelectGroup key={g.label}>
                        <SelectLabel>{g.label}</SelectLabel>
                        {g.items.map((it) => (
                          <SelectItem key={it.value} value={it.value} data-testid={`qr-type-${it.value}`}>
                            {it.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {["url", "doi", "article", "survey", "pdf", "orcid", "email", "phone"].includes(type) && (
                <LabeledInput
                  id="qr-value"
                  label="Destination"
                  data-testid="qr-value-input"
                  placeholder={PLACEHOLDERS[type]}
                  value={fields.value}
                  onChange={(e) => setField("value", e.target.value)}
                />
              )}

              {type === "text" && (
                <div className="space-y-1.5">
                  <Label htmlFor="qr-text">Text</Label>
                  <Textarea id="qr-text" data-testid="qr-text-input" rows={4} placeholder={PLACEHOLDERS.text}
                    value={fields.value} onChange={(e) => setField("value", e.target.value)} />
                </div>
              )}

              {type === "wifi" && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <LabeledInput id="wifi-ssid" label="Network name (SSID)" data-testid="wifi-ssid-input"
                    value={fields.ssid || ""} onChange={(e) => setField("ssid", e.target.value)} />
                  <LabeledInput id="wifi-pass" label="Password" data-testid="wifi-password-input"
                    value={fields.password || ""} onChange={(e) => setField("password", e.target.value)} />
                  <div className="space-y-1.5">
                    <Label htmlFor="wifi-enc">Encryption</Label>
                    <Select value={fields.encryption || "WPA"} onValueChange={(v) => setField("encryption", v)}>
                      <SelectTrigger id="wifi-enc" data-testid="wifi-encryption-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="WPA">WPA / WPA2</SelectItem>
                        <SelectItem value="WEP">WEP</SelectItem>
                        <SelectItem value="nopass">No password</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {type === "vcard" && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <LabeledInput id="vc-name" label="Full name" data-testid="vcard-name-input" value={fields.fullName || ""} onChange={(e) => setField("fullName", e.target.value)} />
                  <LabeledInput id="vc-org" label="Organization / Institution" value={fields.org || ""} onChange={(e) => setField("org", e.target.value)} />
                  <LabeledInput id="vc-title" label="Title" value={fields.title || ""} onChange={(e) => setField("title", e.target.value)} />
                  <LabeledInput id="vc-phone" label="Phone" value={fields.phone || ""} onChange={(e) => setField("phone", e.target.value)} />
                  <LabeledInput id="vc-email" label="Email" value={fields.email || ""} onChange={(e) => setField("email", e.target.value)} />
                  <LabeledInput id="vc-web" label="Website" value={fields.website || ""} onChange={(e) => setField("website", e.target.value)} />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Customization */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="space-y-5 p-6">
            <h3 className="text-base font-semibold text-slate-900">Customize appearance</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="fg">Foreground color</Label>
                <div className="flex items-center gap-2">
                  <input id="fg" type="color" data-testid="qr-fg-color" value={fgColor} onChange={(e) => setFgColor(e.target.value)}
                    className="h-9 w-12 cursor-pointer rounded border border-slate-200" aria-label="Foreground color" />
                  <Input value={fgColor} onChange={(e) => setFgColor(e.target.value)} className="font-mono text-sm" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bg">Background color</Label>
                <div className="flex items-center gap-2">
                  <input id="bg" type="color" data-testid="qr-bg-color" value={bgColor} onChange={(e) => setBgColor(e.target.value)}
                    className="h-9 w-12 cursor-pointer rounded border border-slate-200" aria-label="Background color" />
                  <Input value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="font-mono text-sm" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dot">Module style</Label>
                <Select value={dotType} onValueChange={setDotType}>
                  <SelectTrigger id="dot" data-testid="qr-dot-style"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="square">Square</SelectItem>
                    <SelectItem value="rounded">Rounded</SelectItem>
                    <SelectItem value="dots">Dots</SelectItem>
                    <SelectItem value="extra-rounded">Extra rounded</SelectItem>
                    <SelectItem value="classy">Classy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="corner">Corner style</Label>
                <Select value={cornerType} onValueChange={setCornerType}>
                  <SelectTrigger id="corner" data-testid="qr-corner-style"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="square">Square</SelectItem>
                    <SelectItem value="extra-rounded">Rounded</SelectItem>
                    <SelectItem value="dot">Dot</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ec">Error correction</Label>
                <Select value={ecLevel} onValueChange={setEcLevel}>
                  <SelectTrigger id="ec" data-testid="qr-ec-level"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="L">Low (7%)</SelectItem>
                    <SelectItem value="M">Medium (15%)</SelectItem>
                    <SelectItem value="Q">Quartile (25%)</SelectItem>
                    <SelectItem value="H">High (30%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="res">Download resolution</Label>
                <Select value={String(resolution)} onValueChange={(v) => setResolution(Number(v))}>
                  <SelectTrigger id="res" data-testid="qr-resolution"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="256">256 px</SelectItem>
                    <SelectItem value="512">512 px</SelectItem>
                    <SelectItem value="1024">1024 px</SelectItem>
                    <SelectItem value="2048">2048 px (poster)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Quiet zone (margin): {margin}px</Label>
              <Slider value={[margin]} min={0} max={40} step={2} onValueChange={(v) => setMargin(v[0])} data-testid="qr-margin-slider" aria-label="Quiet zone margin" />
              <p className="text-xs text-slate-500">A quiet zone (empty margin) around the code improves scan reliability. Minimum 16px recommended.</p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                <Upload className="h-4 w-4" /> {logo ? "Change logo" : "Add center logo"}
                <input type="file" accept="image/*" className="hidden" onChange={onLogoUpload} data-testid="qr-logo-upload" />
              </label>
              {logo && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setLogo(null)} data-testid="qr-logo-remove">
                  <X className="mr-1 h-4 w-4" /> Remove logo
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right: preview + warnings + download */}
      <div className={compact ? "space-y-4" : "space-y-4 lg:col-span-2"}>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex flex-col items-center gap-4 p-6">
            <div
              ref={containerRef}
              data-testid="qr-preview"
              className="flex items-center justify-center rounded-lg border border-slate-200 bg-white p-3"
              style={{ minHeight: 220 }}
              aria-label="QR code live preview"
              role="img"
            />
            {(!qrValue || !qrValue.trim()) && (
              <p className="text-center text-sm text-slate-500">Enter content to preview your QR code.</p>
            )}

            {lowContrast && (
              <Alert variant="destructive" data-testid="contrast-warning" className="border-amber-300 bg-amber-50 text-amber-900">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Low contrast ({ratio.toFixed(1)}:1)</AlertTitle>
                <AlertDescription className="text-amber-800">
                  These colors may not scan reliably. Use a dark foreground on a light background (aim for 4:1 or higher).
                </AlertDescription>
              </Alert>
            )}
            {!lowContrast && qrValue && qrValue.trim() && (
              <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-700" data-testid="contrast-ok">
                <CheckCircle2 className="h-4 w-4" /> Good contrast for scanning ({ratio.toFixed(1)}:1)
              </div>
            )}
            {logoNeedsEC && (
              <Alert className="border-slate-200 bg-slate-50 text-slate-700">
                <Info className="h-4 w-4" />
                <AlertDescription className="text-slate-600">
                  A logo covers part of the code. Set error correction to <strong>High</strong> for reliable scanning.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex w-full flex-col gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="w-full bg-[#0D9488] text-white hover:bg-[#0F766E]" disabled={verifying} data-testid="download-qr-btn">
                    <Download className="mr-2 h-4 w-4" />
                    {verifying ? "Verifying…" : "Download QR code"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => doDownload("png")} data-testid="download-png">PNG ({resolution}px)</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => doDownload("svg")} data-testid="download-svg">SVG (vector)</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => doDownload("hires")} data-testid="download-hires">High-res PNG (2048px, poster)</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => doDownload("transparent")} data-testid="download-transparent">Transparent PNG</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <div className="flex items-center justify-center gap-1.5 text-xs text-slate-500">
                <ShieldCheck className="h-3.5 w-3.5 text-[#0D9488]" />
                Every download is decoded and verified before it saves.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
